import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';

import * as flatbuffers from 'flatbuffers';
import { MCSData } from './engine/Serialization/MCSD/m-c-s-data';
import type { CSSProperties } from 'react';
import { useRef, useMemo } from 'react';
import type { IDiscrepancyBehavior, IImportDestination } from './engine/Serialization/Importer.factory';
import { ImportProcessor } from './engine/Serialization/Importer.factory';
import { MCSDImporter } from './engine/Serialization/MCSDImporter';
import { PlanningEntityRegistry } from './engine/DynamicPlanning/PlanningEntityRegistry';
import { PlanningEntityFactory } from './engine/DynamicPlanning/PlanningEntityFactory';
import { createTTVRPlanningEntityKindResolver } from './surgeries/cardiac/TTVR/TTVRPlanningEntityFactoryConfig';
import { TTVRConfigsFactory } from './surgeries/cardiac/TTVR/TTVRConfigsFactory';
import * as TTVR from './surgeries/cardiac/TTVR/TTVRImporterConfig';
import * as TAVR from './surgeries/cardiac/TAVR/TAVRImporterConfig';
import type { ImporterConfig } from './engine/Serialization/IImporter';
import FileSaver from 'file-saver';

const _supportedSurgeryTypes = new Map<string, (mcsd: MCSData) => ImporterConfig>();

_supportedSurgeryTypes.set('TAVR', (/*mcsd: MCSData*/): ImporterConfig => {
  return TAVR.getConfig();
});

_supportedSurgeryTypes.set('TTVR', (mcsd: MCSData): ImporterConfig => {
  const planningEntityFactory = new PlanningEntityFactory(createTTVRPlanningEntityKindResolver());

  const planningEntityRegistry = new PlanningEntityRegistry();
  planningEntityRegistry.init(mcsd, planningEntityFactory);

  const configsFactory = new TTVRConfigsFactory(planningEntityRegistry);
  return configsFactory.getImporterConfig();
});

_supportedSurgeryTypes.set('TTVR_2_0_Alpha', (/*mcsd: MCSData*/): ImporterConfig => {
  const config = TTVR.getConfig();

  // remove
  const meshesToRemove = new Set(['hardware', 'filter', 'calcification', 'vessel_calcification', 'right heart']);
  config.meshes = config.meshes.filter((e) => !meshesToRemove.has(e.importName));

  const splinesToRemove = new Set(['pts_eustachian_ridge_imported', 'pts_commissure_spline_imported']);
  config.splines = config.splines.filter((e) => !splinesToRemove.has(e.algoName)); // !! by algo name

  // patch
  for (const name of ['left atrium', 'left ventricle', 'aorta']) {
    const mesh = config.meshes.find((v) => v.importName == name);
    if (mesh) mesh.isOptionalEntity = false;
  }

  const ptRva = config.points.find((v) => v.importName == 'rva');
  if (ptRva) ptRva.isOptionalEntity = true;

  for (const name of ['svc1_1', 'svc1_2', 'svc1_3', 'ivc1_1', 'ivc1_2', 'ivc1_3']) {
    const pt = config.points.find((v) => v.importName == name);
    if (pt) pt.imagePhaseKind = 'Heart';
  }

  // add
  config.meshes.push({
    importName: 'eustachian ridge',
    algoName: 'eustachian_ridge.descriptor',
    isOptionalEntity: true,
    imagePhaseKind: 'Heart'
  });

  config.points.push(
    {
      importName: 'pvc',
      algoName: 'pt_pulmonary_valve_center_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'papillary 1',
      algoName: 'pt_papillary_1_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'papillary 2',
      algoName: 'pt_papillary_2_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'papillary 3',
      algoName: 'pt_papillary_3_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'as',
      algoName: 'pt_as_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'ap',
      algoName: 'pt_ap_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'ps',
      algoName: 'pt_ps_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'svc2_1',
      algoName: 'pt_svc21_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'svc2_2',
      algoName: 'pt_svc22_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'svc2_3',
      algoName: 'pt_svc23_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'ivc2_1',
      algoName: 'pt_ivc21_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'ivc2_2',
      algoName: 'pt_ivc22_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'ivc2_3',
      algoName: 'pt_ivc23_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'rva',
      algoName: 'pt_rva_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    }
  );

  config.splines.push({
    importName: 'coronary artery',
    algoName: 'pts_coronary_artery_imported',
    isOptionalEntity: false,
    imagePhaseKind: 'Heart'
  });

  return config;
});

class _ImportDestination implements IImportDestination, IDiscrepancyBehavior {
  private _isWarning = false;

  private _isError = false;

  private readonly _log = new Array<string>();

  public constructor(name: string) {
    this._log.push(`Checking [${name}] ...`);
  }

  public registerPhase(): void {
    return;
  }

  public activateAsync(): Promise<void> {
    return Promise.resolve();
  }

  public getLog(): string {
    return this._log.join('\n');
  }

  public onException(e: Error): void {
    this._log.push(`<<< EXCEPTION: ${e.message}`);
    this._isError = true;
  }

  public onDone(
    kindToPhases: Map<string, Array<string>>,
    kindToImports: Map<string, Array<string>>,
    namesResolver: (name: string) => string
  ): void {
    if (this._isError) {
      this._log.push('<<< FAILED');
    } else {
      for (const [kind, phases] of kindToPhases) {
        for (const phase of phases) {
          this._log.push(`  INFO. Imported [${kind}] phase: [${phase}]`);
        }
        for (const entity of kindToImports.get(kind) ?? []) {
          this._log.push(`  INFO. Imported [${kind}] object: [${namesResolver(entity)}]`);
        }
      }
      if (this._isWarning) {
        this._log.push('<<< OK (with warnings)');
      } else {
        this._log.push('<<< OK');
      }
    }
  }

  public onDuplicateEntitiesPresent(
    duplicatesInfo: Map<string, { label: string; duplicates: Array<[string, string]> }>
  ): void {
    for (const [, info] of duplicatesInfo) {
      for (const [label, id] of info.duplicates) {
        this._log.push(`  WARNING. Image stack [${info.label}] contains duplicate entity - skipped: [${label}] ${id}`);
      }
    }
    this._isWarning = this._isWarning || duplicatesInfo.size != 0;
  }

  public onUnlinkedMeshesPresent(unlinkedMeshes: Map<string, string>): void {
    for (const [guid, label] of unlinkedMeshes) {
      this._log.push(`  FATAL. Mesh has no Image link: [${label}] ${guid}`);
    }
    this._isError = this._isError || unlinkedMeshes.size != 0;
  }

  public onImagesWithoutObjectsPresent(imagesWithoutObjects: Map<string, string>): void {
    for (const [guid, label] of imagesWithoutObjects) {
      this._log.push(`  WARNING. Image stack without objects skipped: [${label}] ${guid}`);
    }
    this._isWarning = this._isWarning || imagesWithoutObjects.size != 0;
  }

  public onImagesWithoutWeightPresent(imageKindToPhasesWithoutWeight: Map<string, Map<string, string>>): void {
    for (const [, phases] of imageKindToPhasesWithoutWeight) {
      for (const [guid, label] of phases) {
        this._log.push(`  WARNING. Image stack without weight skipped: [${label}] ${guid}`);
      }
    }
    this._isWarning = this._isWarning || imageKindToPhasesWithoutWeight.size != 0;
  }

  public onKindsWithoutImagesPresent(imageKindsWithoutImages: Map<string, boolean>): void {
    for (const [kind, isOptional] of imageKindsWithoutImages) {
      if (isOptional) {
        this._isWarning = true;
        this._log.push(`  WARNING. Optional image kind has no images: [${kind}]`);
      } else {
        this._isError = true;
        this._log.push(`  FATAL. Obligatory image kind has no images: [${kind}]`);
      }
    }
  }

  public onPhaseEntitiesDiscrepancy(absentEntityToNumPhases: Map<string, number>): void {
    for (const [entityName, numOfPhasesWithoutIt] of absentEntityToNumPhases) {
      this._log.push(
        `  WARNING. [${entityName}] object skipped during import as it is absent on ${numOfPhasesWithoutIt} phase(s).`
      );
    }
    this._isWarning = this._isWarning || absentEntityToNumPhases.size != 0;
  }

  public onObligatoryEntitiesMissing(imageKindToMissingEntities: Map<string, Array<string>>): void {
    for (const [kind, missingObligatoryEntities] of imageKindToMissingEntities) {
      for (const missingObligatoryEntity of missingObligatoryEntities) {
        this._log.push(`  ERROR. Obligatory object for [${kind}] is missing: [${missingObligatoryEntity}]`);
      }
    }
    this._isError = this._isError || imageKindToMissingEntities.size != 0;
  }

  public onOptionalEntitiesMissing(imageKindToMissingOptionalEntities: Map<string, Array<string>>): void {
    for (const [kind, missingOptionalEntities] of imageKindToMissingOptionalEntities) {
      for (const missingOptionalEntity of missingOptionalEntities) {
        this._log.push(`  INFO. Optional object for [${kind}] is missing: [${missingOptionalEntity}]`);
      }
    }
  }
}

async function _ValidateFile(surgeryType: string, file: File): Promise<string> {
  const destination = new _ImportDestination(file.name);
  try {
    const configGetter = _supportedSurgeryTypes.get(surgeryType);
    if (!configGetter) {
      throw new Error('Logic Error');
    }
    const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (): void => resolve(reader.result as ArrayBuffer);
      reader.onerror = (): void => reject(new Error('FileReader error'));
      reader.onabort = (): void => reject(new Error('FileReader abort'));
      reader.readAsArrayBuffer(file);
    });

    const data = new Uint8Array(buffer);
    const buf = new flatbuffers.ByteBuffer(data);
    const mcsd = MCSData.getRootAsMCSData(buf);
    const processor = new ImportProcessor(destination, [new MCSDImporter(() => Promise.resolve(mcsd))], destination);

    await processor.processImport(configGetter(mcsd));
  } catch (e: unknown) {
    destination.onException(e as Error);
  }
  return destination.getLog();
}

function Validator(): JSX.Element {
  const { surgeryType } = useParams();

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { /*acceptedFiles, fileRejections,*/ getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: {
        'application/x-binary': ['.mcsd']
      },
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
      onDrop: async (files: Array<File>) => {
        if (textAreaRef.current !== null) {
          textAreaRef.current.value = '';
        }
        for (let i = 0, n = files.length; i < n; ++i) {
          const file = files[i];
          const log = await _ValidateFile(surgeryType ?? '', file);
          if (textAreaRef.current !== null) {
            textAreaRef.current.value += log;
            if (n > 1) {
              textAreaRef.current.value += ` (${i + 1} of ${n})`;
            }
            if (i < n - 1) {
              textAreaRef.current.value += '\n\n';
            }
          }
          const blob = new Blob([log], { type: 'text/plain' });
          const fileName = file.name.substring(0, file.name.lastIndexOf('.mcsd')).concat('.result.log');
          FileSaver.saveAs(blob, fileName);
        }
      }
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  return (
    <>
      {surgeryType && _supportedSurgeryTypes.has(surgeryType) ? (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <h1>Surgery Type: {surgeryType}</h1>
          <br />

          <div className="container">
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} accept=".mcsd" />
              <p>Drag-n-drop .MCSD files here, or click to use open files selection dialog</p>
            </div>
          </div>
          <br />
          <h2>Output:</h2>
          <div style={{ flexGrow: 1 }}>
            <textarea ref={textAreaRef} readOnly={true} style={{ width: '100%', height: '100%' }}></textarea>
          </div>
        </div>
      ) : (
        <div>
          <h1>Unknown Surgery Type: {surgeryType}</h1>
          <i>Supported Surgery Types: TAVR, TTVR</i>
        </div>
      )}
    </>
  );
}

export default Validator;

const baseStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out'
};

const focusedStyle: CSSProperties = {
  borderColor: '#2196f3'
};

const acceptStyle: CSSProperties = {
  borderColor: '#00e676'
};

const rejectStyle: CSSProperties = {
  borderColor: '#ff1744'
};
